import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SportsSoccerOutlinedIcon from "@mui/icons-material/SportsSoccerOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { Location } from "history";
import { useTranslation } from "react-i18next";
import homeImage from "../../assets/images/homeImage2.jpg";
import strings from "../../global/constants/StringConstants";
import urls from "../../global/constants/UrlConstants";
import { selectAuthenticated } from "../../redux/authSlice";
import history from "../../utils/history";
import { useAppSelector } from "../../utils/hooks";
import AppFooter from "../Shared/AppFooter/AppFooter";
import AppHeader from "../Shared/AppHeader/AppHeader";
import homeStyles from "./Home.styles";

interface CustomProps {
  location?: Location;
}

interface FeatureItem {
  tooltip: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Icon: React.ElementType<any>;
  header: string;
  category: string;
  color: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  analysis: "#9053F6",
  predictions: "#6366F1",
  charts: "#3B82F6",
  health: "#10B981",
  social: "#EC4899",
};

// Ordered category list — drives column rendering in Option D
const CATEGORY_ORDER = [
  "analysis",
  "predictions",
  "charts",
  "health",
  "social",
] as const;

// Zodiac symbol + ordinal per category — purely decorative, no i18n needed
const CATEGORY_META: Record<string, { symbol: string; num: string }> = {
  analysis: { symbol: "♈", num: "01" },
  predictions: { symbol: "♋", num: "02" },
  charts: { symbol: "☿", num: "03" },
  health: { symbol: "♑", num: "04" },
  social: { symbol: "♀", num: "05" },
};

const STATS = [
  { number: "10K+", labelKey: "statsUsers" },
  { number: "13", labelKey: "statsTools" },
  { number: "2", labelKey: "statsLanguages" },
  { number: "100%", labelKey: "statsVedic" },
] as const;

const AVATAR_COLORS = ["#9053F6", "#6366F1", "#EC4899", "#10B981"];

const Home = (props: CustomProps) => {
  const classes = homeStyles;
  const isAuthenticated = useAppSelector(selectAuthenticated);
  const { t } = useTranslation();
  // focusedIdx / focusedFeature used by Option A & B — restore if switching back
  // const [focusedIdx, setFocusedIdx] = useState<number>(0);

  const allFeatures: FeatureItem[] = [
    {
      tooltip: "analysisTooltip",
      Icon: PsychologyOutlinedIcon,
      header: "analysisHeader",
      category: "analysis",
      color: "#9053F6",
    },
    {
      tooltip: "arudhaPadaTooltip",
      Icon: VisibilityOutlinedIcon,
      header: "arudhaPadaHeader",
      category: "analysis",
      color: "#A78BFA",
    },
    {
      tooltip: "shadbalaTooltip",
      Icon: BoltOutlinedIcon,
      header: "shadbalaHeader",
      category: "analysis",
      color: "#7C3AED",
    },
    {
      tooltip: "mahadashaTooltip",
      Icon: TimelineOutlinedIcon,
      header: "mahadashaHeader",
      category: "predictions",
      color: "#6366F1",
    },
    {
      tooltip: "nakshashtraTooltip",
      Icon: AutoAwesomeOutlinedIcon,
      header: "nakshatraHeader",
      category: "predictions",
      color: "#818CF8",
    },
    {
      tooltip: "currentNakshatraTooltip",
      Icon: GpsFixedOutlinedIcon,
      header: "currentNakshatraHeader",
      category: "predictions",
      color: "#06B6D4",
    },
    {
      tooltip: "rahuTransitsTooltip",
      Icon: ExploreOutlinedIcon,
      header: "rahuTransitsHeader",
      category: "predictions",
      color: "#0EA5E9",
    },
    {
      tooltip: "circularChartTooltip",
      Icon: DonutLargeOutlinedIcon,
      header: "circularChartHeader",
      category: "charts",
      color: "#3B82F6",
    },
    {
      tooltip: "planetDetailsTooltip",
      Icon: PublicOutlinedIcon,
      header: "planetHeader",
      category: "charts",
      color: "#60A5FA",
    },
    {
      tooltip: "mineralsTooltip",
      Icon: ScienceOutlinedIcon,
      header: "mineralsHeader",
      category: "health",
      color: "#10B981",
    },
    {
      tooltip: "footballPositionTooltip",
      Icon: SportsSoccerOutlinedIcon,
      header: "footballHeader",
      category: "health",
      color: "#14B8A6",
    },
    {
      tooltip: "vedazTalkTooltip",
      Icon: ForumOutlinedIcon,
      header: "vedazTalk",
      category: "social",
      color: "#F97316",
    },
    {
      tooltip: "compatibilityTooltip",
      Icon: FavoriteOutlinedIcon,
      header: "compatibility",
      category: "social",
      color: "#EC4899",
    },
  ];

  const TAB_LABELS: Record<string, string> = {
    analysis: t("featureTabAnalysis"),
    predictions: t("featureTabPredictions"),
    charts: t("featureTabCharts"),
    health: t("featureTabHealth"),
    social: t("featureTabSocial"),
  };

  // const focusedFeature = allFeatures[focusedIdx] ?? allFeatures[0]; // Option A/B
  // const strip1 = allFeatures.slice(0, 7); // Option A
  // const strip2 = allFeatures.slice(7);    // Option A

  if (isAuthenticated) {
    if (
      props.location?.pathname?.split("/")[1].toLowerCase() !==
      strings.RESET_PASSWORD
    ) {
      history.push(urls.dashboardViewPath);
      return null;
    }
  }

  return (
    <>
      <AppHeader />

      <Box sx={classes.pageWrapper}>
        {/* ── Fixed aurora background ──────────────────────────────── */}
        <Box sx={classes.auroraWrap}>
          <Box sx={classes.auroraA} />
          <Box sx={classes.auroraB} />
          <Box sx={classes.auroraC} />
        </Box>

        {/* ── Hero ─────────────────────────────────────────────────── */}
        <Box sx={classes.heroSection}>
          <Box sx={classes.heroDotGrid} />

          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            <Grid
              container
              alignItems="center"
              spacing={{ md: 6, sm: 4, xs: 3 }}
            >
              {/* Left: text */}
              <Grid item xl={6} lg={6} md={7} sm={12} xs={12}>
                <Box sx={classes.heroBadge}>✦&nbsp;{t("heroEyebrow")}</Box>

                <Typography sx={classes.heroTagline}>
                  {t("heroTagline")}
                </Typography>

                <Typography component="h1" sx={classes.heroHeadline}>
                  {t("homeHeader")}
                </Typography>

                <Typography sx={classes.heroSubtitle}>
                  {t("homeContent")}
                </Typography>

                {/* Social proof */}
                {/* <Box sx={classes.heroSocialProof}>
                  <Box sx={classes.socialAvatarStack}>
                    {AVATAR_COLORS.map((bg, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: bg,
                          border: "2px solid #060612",
                          ml: i > 0 ? "-10px" : 0,
                          zIndex: AVATAR_COLORS.length - i,
                          position: "relative",
                          boxShadow: `0 0 8px ${bg}70`,
                        }}
                      />
                    ))}
                  </Box>
                  <Typography sx={classes.socialProofText}>
                    <strong>10,000+</strong>&nbsp;{t("heroSocialProof")}
                  </Typography>
                </Box> */}

                <Box sx={classes.ctaRow}>
                  <Button
                    sx={classes.primaryBtn}
                    onClick={() => history.push(urls.registerViewPath)}
                  >
                    {t("signup")}
                  </Button>
                  <Button
                    sx={classes.secondaryBtn}
                    onClick={() => history.push(urls.loginViewPath)}
                  >
                    {t("login")}
                  </Button>
                </Box>
              </Grid>

              {/* Right: image + decorations */}
              <Grid
                item
                xl={6}
                lg={6}
                md={5}
                sm={12}
                xs={12}
                sx={classes.heroImageCol}
              >
                <Box sx={classes.heroImageWrapper}>
                  <Box sx={classes.heroGlowRing} />
                  <Box sx={classes.heroOrbitRing1} />
                  <Box sx={classes.heroOrbitRing2} />

                  {/* <Box
                    sx={{
                      ...classes.floatingChipBase,
                      ...classes.floatingChip1,
                    }}
                  >
                    <Box sx={classes.chipSymbol}>☉</Box>
                    <Box>
                      <Typography sx={classes.chipText}>Birth Chart</Typography>
                      <Typography sx={classes.chipSubText}>
                        Vedic Precision
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      ...classes.floatingChipBase,
                      ...classes.floatingChip2,
                    }}
                  >
                    <Box sx={classes.chipSymbol}>♃</Box>
                    <Box>
                      <Typography sx={classes.chipText}>
                        Jupiter Strong
                      </Typography>
                      <Typography sx={classes.chipSubText}>
                        Shadbala Analysis
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      ...classes.floatingChipBase,
                      ...classes.floatingChip3,
                    }}
                  >
                    <Box
                      sx={{
                        ...classes.chipSymbol,
                        fontSize: "16px",
                        color: "#10B981",
                      }}
                    >
                      ✦
                    </Box>
                    <Box>
                      <Typography sx={classes.chipText}>13 Features</Typography>
                      <Typography sx={classes.chipSubText}>
                        All in one platform
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      ...classes.floatingChipBase,
                      ...classes.floatingChip4,
                    }}
                  >
                    <Box
                      sx={{
                        ...classes.chipSymbol,
                        fontSize: "16px",
                        color: "#A78BFA",
                      }}
                    >
                      ♋
                    </Box>
                    <Box>
                      <Typography sx={classes.chipText}>
                        98% Accuracy
                      </Typography>
                      <Typography sx={classes.chipSubText}>
                        Vedic algorithms
                      </Typography>
                    </Box>
                  </Box> */}

                  {/* Main image */}
                  <Box sx={classes.heroImageBox}>
                    <img
                      src={homeImage}
                      width="100%"
                      style={{
                        maxWidth: "520px",
                        display: "block",
                        margin: "0 auto",
                        border: "1px solid #7060f26b",
                        borderRadius: "10px",
                      }}
                      alt="Astrology illustration"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* ── Stats strip ──────────────────────────────────────────── */}
        <Box sx={classes.statsStrip}>
          <Container maxWidth="md">
            <Box sx={classes.statsInner}>
              {STATS.map((s) => (
                <Box key={s.labelKey} sx={classes.statItem}>
                  <Typography sx={classes.statNumber}>{s.number}</Typography>
                  <Typography sx={classes.statLabel}>
                    {t(s.labelKey)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ── Features ─────────────────────────────────────────────── */}
        <Box sx={classes.featuresSection}>
          <Container maxWidth="xl">
            <Box sx={classes.sectionHeadingBox}>
              <Typography sx={classes.sectionEyebrow}>
                ✦&nbsp;{t("featuresTitle")}
              </Typography>
              <Typography component="h2" sx={classes.sectionTitle}>
                {t("featuresTitle")}
              </Typography>
              <Typography sx={classes.sectionSubtitle}>
                {t("featuresSubtitle")}
              </Typography>
            </Box>

            {/* ── OPTION A (commented out) — Infinite marquee strips + spotlight
            <Box sx={{ overflow: "hidden", maskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)", WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)", py: 1 }}>
              marquee strip 1 scrolls left, strip 2 scrolls right,
              each pill onMouseEnter sets focusedIdx,
              spotlight panel below reacts live.
              See OPTION A styles in Home.styles.ts for full implementation.
            </Box>
            ── END OPTION A ── */}

            {/* ── OPTION B (commented out) — Constellation orrery wheel
                See OPTION B styles in Home.styles.ts for full implementation.
            ── END OPTION B ── */}

            {/* ── OPTION C (commented out) — Stacked glowing reveal strips
                13 full-width rows: timeline spine, number dot, icon badge,
                name + full desc, category pill. All visible, no interaction needed.
                See OPTION C styles in Home.styles.ts.
            ── END OPTION C ── */}

            {/* ── OPTION D (commented out) — Celestial Columns
                5 proportional pillars (3fr·4fr·2fr·2fr·2fr).
                See OPTION D styles in Home.styles.ts.
            ── END OPTION D ── */}
          </Container>

          {/* ── OPTION E: Spectral Crystal Bands ─────────────────────── */}
          {/* 5 full-width diagonal slabs — one per category. skewY(-2deg)
              makes each band a tilted "crystal plate". Content counter-skewed
              to stay flat. ALL features, titles, descriptions always visible.
              Slab backgrounds overlap by 40px — creates clean diagonal seams
              (the "crystal fracture lines") between category zones.           */}
          <Box sx={classes.featureSpectraWrap}>
            {CATEGORY_ORDER.map((cat, catIdx) => {
              const catColor = CATEGORY_COLORS[cat] ?? "#9053F6";
              const meta = CATEGORY_META[cat];
              const catFeatures = allFeatures.filter((f) => f.category === cat);
              // Each band visually "cuts over" the previous — later bands render
              // on top (natural DOM stacking) covering the previous band's bottom
              const zIndex = CATEGORY_ORDER.length - catIdx;
              return (
                <Box
                  key={cat}
                  sx={{
                    ...classes.featureSpectraBand,
                    mt: catIdx === 0 ? 0 : classes.featureSpectraBand.mt,
                    zIndex,
                    background: `linear-gradient(105deg, ${catColor}14 0%, rgba(4,4,16,0.97) 55%)`,
                    borderTop: `1px solid ${catColor}1A`,
                  }}
                >
                  <Box sx={classes.featureSpectraBandInner}>
                    {/* ── Left: category identity panel ── */}
                    <Box sx={classes.featureSpectraCatPanel}>
                      <Typography
                        sx={{
                          ...classes.featureSpectraCatGlyph,
                          color: catColor,
                        }}
                      >
                        {meta.symbol}
                      </Typography>
                      <Typography
                        sx={{
                          ...classes.featureSpectraCatName,
                          color: catColor,
                        }}
                      >
                        {TAB_LABELS[cat]}
                      </Typography>
                      <Typography sx={classes.featureSpectraCatCount}>
                        {catFeatures.length}&thinsp;✦&thinsp;features
                      </Typography>
                      <Box
                        sx={{
                          ...classes.featureSpectraCatRule,
                          background: `linear-gradient(90deg, ${catColor}, transparent)`,
                        }}
                      />
                    </Box>

                    {/* ── Right: feature items ── */}
                    <Box sx={classes.featureSpectraFeatures}>
                      {catFeatures.map((feature, fIdx) => (
                        <Box key={fIdx} sx={classes.featureSpectraItem}>
                          <Box
                            sx={{
                              ...classes.featureSpectraIcon,
                              background: `${feature.color}1E`,
                              border: `1px solid ${feature.color}40`,
                              boxShadow: `0 0 18px ${feature.color}1A`,
                              "&:hover": {
                                ...(classes.featureSpectraIcon as any)[
                                  "&:hover"
                                ],
                                boxShadow: `0 0 32px ${feature.color}55`,
                              },
                            }}
                          >
                            <feature.Icon
                              sx={{
                                fontSize: { md: "22px", xs: "18px" },
                                color: feature.color,
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography sx={classes.featureSpectraName}>
                              {t(feature.header)}
                            </Typography>
                            <Typography sx={classes.featureSpectraDesc}>
                              {t(feature.tooltip)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
          {/* ── END OPTION E ── */}
        </Box>

        {/* ── Bottom CTA ───────────────────────────────────────────── */}
        <Box sx={classes.bottomSection}>
          <Container maxWidth="md">
            {/* Zodiac row */}
            <Typography sx={classes.zodiacRow}>
              ♈&nbsp;♉&nbsp;♊&nbsp;♋&nbsp;♌&nbsp;♍&nbsp;♎&nbsp;♏&nbsp;♐&nbsp;♑&nbsp;♒&nbsp;♓
            </Typography>

            <Box sx={classes.bottomCard}>
              {/* 3 benefit columns */}
              <Box sx={classes.benefitRow}>
                {(
                  [
                    {
                      Icon: FlashOnOutlinedIcon,
                      labelKey: "ctaBenefitA",
                      color: "#9053F6",
                    },
                    {
                      Icon: WidgetsOutlinedIcon,
                      labelKey: "ctaBenefitB",
                      color: "#6366F1",
                    },
                    {
                      Icon: LockOutlinedIcon,
                      labelKey: "ctaBenefitC",
                      color: "#10B981",
                    },
                  ] as const
                ).map(({ Icon, labelKey, color }) => (
                  <Box key={labelKey} sx={classes.benefitItem}>
                    <Box
                      sx={{
                        ...classes.benefitIconBox,
                        background: `${color}22`,
                        border: `1px solid ${color}40`,
                        boxShadow: `0 4px 16px ${color}20`,
                      }}
                    >
                      <Icon sx={{ fontSize: "22px", color }} />
                    </Box>
                    <Typography sx={classes.benefitLabel}>
                      {t(labelKey)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={classes.bottomDivider} />

              <Typography component="h2" sx={classes.bottomCardTitle}>
                {t("ctaHeading")}
              </Typography>
              <Typography sx={classes.instructions}>
                {t("ctaSubheading")}
              </Typography>

              <Box sx={{ ...classes.ctaRow, justifyContent: "center" }}>
                <Button
                  sx={classes.primaryBtn}
                  onClick={() => history.push(urls.registerViewPath)}
                >
                  {t("signup")}
                </Button>
                <Button
                  sx={classes.secondaryBtn}
                  onClick={() => history.push(urls.loginViewPath)}
                >
                  {t("login")}
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>

      <AppFooter />
    </>
  );
};

export default Home;
